import React from 'react';
import { Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supportedLanguages } from '@/utils/supportedLanguages';
import { cn } from '@/lib/utils';

interface LanguageSelectorProps {
  className?: string;
}

/**
 * Lightweight language picker for pages without an authenticated user
 * (login, verify email, password reset). The choice is persisted by the
 * i18next language detector (localStorage + cookie); once signed in the
 * user's stored preference takes over.
 */
const LanguageSelector: React.FC<LanguageSelectorProps> = ({ className }) => {
  const { i18n, t } = useTranslation();

  const current =
    supportedLanguages.find(
      lang => lang.code === (i18n.resolvedLanguage ?? i18n.language)
    ) ?? supportedLanguages[0];
  const currentLanguage = current.code;

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  return (
    <Select onValueChange={changeLanguage} value={currentLanguage}>
      <SelectTrigger
        variant="borderless"
        aria-label={t('profile.select_language.translation')}
        className={cn(
          'w-auto gap-2 bg-transparent px-2 text-sm text-muted-foreground hover:text-foreground',
          className
        )}
      >
        <Globe className="h-4 w-4" />
        {/* The label is passed in rather than left to `SelectValue` to resolve
            from its items: that resolution happens when the items register
            themselves on mount, so a server-rendered trigger comes out empty
            and then gains text on hydration. On the prerendered docs site that
            counts as a mismatch and costs the whole prerendered tree. The
            string is the same one the items carry. */}
        <SelectValue placeholder={t('profile.select_language.translation')}>
          {current.name}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="rounded-[20px]">
        {supportedLanguages.map(lang => (
          <SelectItem key={lang.code} value={lang.code}>
            {lang.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default LanguageSelector;
